import { CClient } from '@common/utils'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import useDetectCountry from '@hooks/useDetectCountry'
import { Trans } from '@lingui/macro'
import React, { useEffect, useState } from 'react'
import { Check, X } from 'react-feather'
import * as Sentry from '@sentry/node'
import { FastlyImage } from '@components/common/image'

const CODINGAL_OFFERING = {
  prime: {
    title: 'Prime',
    subtitle: 'Private 1-on-1 tutoring',
    color: '#EB5757',
    discount: 0.35,
    points: [
      {
        offering: true,
        content: <>1:1 personalized live classes (45 minutes each)</>,
      },
      {
        offering: true,
        content: (
          <>
            2–3 lessons per week — flexible scheduling tailored for your child
          </>
        ),
      },
      {
        offering: true,
        content: <>Personalized attention in every class</>,
      },
      {
        offering: true,
        content: (
          <>
            <b>Unlimited rescheduling</b> at the student’s convenience
          </>
        ),
      },
    ],
  },
  premier: {
    title: 'Premier',
    subtitle: 'Micro group (2–3 students)',
    color: '#9B51E0',
    discount: 0.3,
    points: [
      {
        offering: true,
        content: <>Personalized live group classes (60 minutes each)</>,
      },
      {
        offering: true,
        content: <>2 lessons per week — 8 fun & engaging classes per month</>,
      },
      {
        offering: true,
        content: <>Focused attention in every class</>,
      },
      {
        offering: true,
        content: <>Reschedule mutually with your class buddies</>,
      },
    ],
  },
  plus: {
    title: 'Plus',
    subtitle: 'Small group (4–5 students)',
    color: '#2D9CDB',
    discount: 0.25,
    points: [
      {
        offering: true,
        content: <>Fun & engaging live group classes (60 minutes each)</>,
      },
      {
        offering: true,
        content: <>2 lessons per week — 8 fun & engaging classes per month</>,
      },
      {
        offering: true,
        content: <>Focused attention in every class</>,
      },
      {
        offering: false,
        content: <>No rescheduling of classes available</>,
      },
    ],
  },
}
const COURSE_FEATURES = [
  <>
    Learn from <strong>handpicked top IT coding instructors</strong> chosen just
    for you.
  </>,
  <>
    Enjoy <strong>unlimited doubt sessions</strong>, available 24x7.
  </>,
  <>
    Access a <strong>dedicated support team</strong> to resolve queries 24x7.
  </>,
  <>
    <strong>Lifetime access</strong> to all class recordings.
  </>,
  <>
    Earn a{' '}
    <strong>
      <u>STEM.org accredited certificate</u>
    </strong>{' '}
    upon course completion.
  </>,
  <>
    Work on <strong>projects and quizzes</strong> after every class.
  </>,
  <>
    Receive <strong>monthly progress reports</strong> and attend parent-teacher
    meetings (PTMs).
  </>,
  <>
    <strong>Flexible payment options</strong> available.
  </>,
  <>
    Follow a <strong>tailored curriculum</strong> designed for your child&apos;s
    interests and pace.
  </>,
  <>
    Get a <strong>100% money-back guarantee</strong> for assured satisfaction.
  </>,
  <>
    Enjoy a <strong>gamified learning dashboard</strong> filled with engaging
    projects and quizzes.
  </>,
]

export type CodingalSubscriptionType = {
  isCoursePage?: boolean
  urlQuery?: string
  subject?: 'Coding' | 'Math'
}

const CodingalSubscription = ({
  subject = 'Coding',
}: CodingalSubscriptionType) => {
  const { countryCode, currency } = useDetectCountry()
  const subsCode = Object.keys(CODINGAL_OFFERING)
  const [prices, setPrices] = useState<any>()
  async function handleFunctionPrices() {
    try {
      const q = `?country_code=${
        countryCode ?? 'us'
      }&subject=${subject}&currency_code=${currency ?? 'USD'}`
      const endpoint = `/api/v1/pricing/service-prices/${q}`
      const response = await CClient(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      if (response.status === 200) {
        const data = await response.json()
        setPrices(data.prices)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  useEffect(() => {
    if (countryCode) handleFunctionPrices()
  }, [countryCode])

  function formatCurrency(amount?: number, currencyCode?: string) {
    try {
      const currencySymbol = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode ?? 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)

      const formattedAmount = currencySymbol.replace(/(\D+)(\d+)/, '$1 $2')

      if (formattedAmount.endsWith('.00')) {
        return formattedAmount.slice(0, -3)
      }
      return formattedAmount
    } catch (error) {
      return amount
    }
  }
  // Determine the default offering based on countryCode or explicitly provided offeringType

  // Initialize activeTab with logic to ensure 'plus' is selected for 'both'

  // Set the codingalSubscription based on the activeTab

  // Set activeTab to 'plus' if defaultSubscriptionType is 'both'

  return (
    <>
      {prices && (
        <div className="text-grey">
          <div className="container mx-auto my-12 lg:my-16 px-5 text-grey flex flex-col items-center gap-4">
            <h2 className="text-2xl lg:text-4xl font-600 mb-4 max-w-4xl mx-auto text-center">
              <Trans>
                3 flexible plans to suit your{' '}
                <span className="yellow-underline">
                  child’s learning style & schedule
                </span>
              </Trans>
            </h2>
            {/** Disaplay Tab only if the courseofferingType is both */}
            <div className="w-full gap-5 flex flex-col lg:flex-row justify-center">
              {subsCode.map((e, i) => {
                const offer = CODINGAL_OFFERING[e]
                if (!prices || !prices[e]) {
                  return null
                }
                const cost = prices[e].pricePerSession
                const offerPrice = cost * (1 - offer.discount)
                const discountPercentage = offer.discount * 100

                return (
                  <div
                    key={i}
                    className="flex flex-col w-full rounded-lg bg-white border overflow-hidden relative items-center shadow hover:shadow-lg transition-all duration-200 "
                    style={{ borderColor: offer.color }}
                  >
                    <h2
                      className="py-2 leading-tight font-600 w-full text-2xl lg:text-3xl text-center text-white"
                      style={{ backgroundColor: offer.color }}
                    >
                      {offer.title}
                    </h2>
                    <div
                      style={{ backgroundColor: `${offer.color}1A` }}
                      className="p-5 w-full flex flex-col items-center gap-2"
                    >
                      {prices && prices[e].pricePerSession !== 0 && (
                        <div className="space-y-4">
                          <h2 className="leading-tight font-600 w-full text-2xl lg:text-3xl text-center">
                            {formatCurrency(offerPrice, currency)} / session
                          </h2>

                          <div className="leading-tight font-600 w-full lg:text-2xl text-lg text-center text-grey-800 line-through">
                            {formatCurrency(cost, currency)}
                          </div>
                        </div>
                      )}

                      <div className="px-4 py-1 bg-white text-grey-800 font-600 text-lg rounded-full mx-auto">
                        {discountPercentage}% off
                      </div>
                    </div>
                    <div className="mt-5 px-8 mx-auto w-full">
                      <BookFreeClassBtn fullWidth size="md" variant="outline" />
                    </div>
                    <p className="text-lg font-600 underline text-center pt-4 px-3">
                      {offer.subtitle}
                    </p>
                    <div className="flex flex-col gap-2 p-4">
                      {offer.points.map((point, index) => (
                        <div
                          key={index}
                          className="inline-flex items-baseline gap-2"
                        >
                          <span
                            className={`rounded-full inline-flex ${
                              point.offering ? 'bg-green-100' : 'bg-red-200'
                            }`}
                            style={{ padding: '2px' }}
                          >
                            {point.offering ? (
                              <Check
                                height={13}
                                width={13}
                                strokeWidth={3}
                                className="text-white"
                              />
                            ) : (
                              <X
                                height={13}
                                width={13}
                                strokeWidth={3}
                                className="text-white"
                              />
                            )}{' '}
                          </span>
                          <span className="text-lg font-400">
                            {point.content}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex flex-col items-center mt-8">
              <div>
                <FastlyImage
                  src={`/images/home/cody.png`}
                  webpSrc={`/images/home/cody.webp`}
                  alt={'cody'}
                  height={111}
                  width={97}
                  className="h-13  object-cover object-top"
                />
              </div>
              <span className="bg-green-50 border border-green-300 p-4 px-8 rounded-xl text-lg font-600">
                <span>
                  Complete a free trial lesson to unlock additional
                  scholarships!
                </span>
              </span>
            </div>

            <div className="container p-8 md:pt-12 md:pl-12 border border-blue-100 rounded-2xl bg-blue-50 bg-opacity-25 space-y-4 relative">
              <div className="absolute top-0 left-0 p-2 hidden md:block">
                <FastlyImage
                  src={`/images/home/shine-star.png`}
                  // webpSrc={`/images/home/cody.webp`}
                  alt={'shine-star'}
                  className="h-13  object-cover object-top"
                />
              </div>
              <div className="space-y-2">
                <p className="text-xl font-600 mt-4">
                  Every student at Codingal gets these amazing benefits!
                </p>
                <ul className="pl-8">
                  {COURSE_FEATURES.map((feature, index) => (
                    <li
                      key={index}
                      className="text-lg font-400 mb-2 text-blue-200"
                    >
                      <span className="text-grey">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-2 text-center">
              Start with a free trial lesson at your preferred date & time. No
              commitments, fees, or credit card required.
            </p>
            <div className="flex justify-center mt-2 w-full">
              <BookFreeClassBtn size="xl" variant="solid" />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default React.memo(CodingalSubscription)
